import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ItfDatabaseService } from '../../services/itf-database.service';
import { EstudianteService } from '../../services/estudiante.service';
import { SharedService } from '../../services/shared.service';
import { ItfDatabase } from '../../models/itf-database.model';
import { ItfPago } from '../../models/itf-pago.model';
import { Estudiante } from '../../models/estudiante.model';
import { Graduacion } from '../../models/graduacion.model';

@Component({
  selector: 'app-itf-database',
  templateUrl: './itf-database.component.html',
  styleUrls: ['./itf-database.component.css']
})
export class ItfDatabaseComponent implements OnInit {
  Math = Math;

  @Input() prefilledLibreta: number | null = null;
  
  searchMode: 'simple' | 'complex' = 'simple';
  
  // Simple search
  searchType: 'libreta' | 'documento' = 'libreta';
  nroLibreta: number | null = null;
  nroDocumento: string = '';
  
  itfRecord: ItfDatabase | null = null;
  isNew: boolean = false;
  error: string | null = null;
  showRegisterPrompt: boolean = false;
  estudianteData: Estudiante | null = null;
  graduationWarning: string | null = null;

  // Complex search
  searchForm: FormGroup;
  itfRecords: ItfDatabase[] = [];
  filteredRecords: ItfDatabase[] = [];
  loading: boolean = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  
  // Modal state
  selectedRecord: ItfDatabase | null = null;
  selectedEstudiante: Estudiante | null = null;
  showItfDetail: boolean = false;
  showEstudianteDetail: boolean = false;
  // Payment modal state
  showPaymentModal: boolean = false;
  selectedRecordForPayment: ItfDatabase | null = null;
  
  // Pagination
  pageSize: number = 50;
  currentPage: number = 1;
  totalPages: number = 1;
  paginatedRecords: ItfDatabase[] = [];
  
  // Sorting
  sortBy: string = 'nroLibreta';
  sortOrder: 'asc' | 'desc' = 'asc';
  availableSortFields = [
    'nroLibreta', 'apellido', 'nombre', 'fcNacimiento', 'sexo',
    'graduacion', 'nuCertDan', 'ngb', 'nacionalidad', 'emailNgb',
    'matriculaVigente', 'matriculaAnio', 'createdAt', 'updatedAt'
  ];

  

  // Data sources
  private graduaciones: Graduacion[] = [];
  gupList: Array<{ value: number; label: string }> = [];
  danList: Array<{ value: number; label: string }> = [];

  // Date limits
  readonly TODAY = new Date().toISOString().split('T')[0];
  readonly MIN_BIRTH_DATE = '1900-01-01';

  constructor(
    private fb: FormBuilder,
    private itfDatabaseService: ItfDatabaseService,
    private estudianteService: EstudianteService,
    private sharedService: SharedService
  ) {
    this.searchForm = this.fb.group({
      nroLibretaDesde: ['', [Validators.min(1)]],
      nroLibretaHasta: [''],
      nombre: [''],
      apellido: [''],
      fcNacimientoDesde: [''],
      fcNacimientoHasta: [''],
      sexo: [''],
      gradoTipo: [''], // GUP, DAN, TODOS
      gup: [''],
      dan: [''],
      nuCertDan: [''],
      ngb: [''],
      nacionalidad: [''],
      emailNgb: [''],
      matriculaVigente: [''], // SI, NO, TODOS
      matriculaAnio: ['']
    });

    this.setupRangeValidation();
  }

  ngOnInit(): void {
    this.sharedService.graduaciones$.subscribe(graduaciones => {
      this.graduaciones = graduaciones;
      this.buildGraduacionLists();
    });

    // If prefilled libreta is provided, auto-search
    if (this.prefilledLibreta) {
      this.searchMode = 'simple';
      this.searchType = 'libreta';
      this.nroLibreta = this.prefilledLibreta;
      this.buscarPorNroLibreta();
    }
  }

  // ============================================================================
  // GRADUACION LISTS
  // ============================================================================

  private buildGraduacionLists(): void {
    // Build GUP list (10 to 1)
    this.gupList = [];
    for (let gup = 10; gup >= 1; gup--) {
      const graduacion = this.graduaciones.find(g => g.gup === gup);
      if (graduacion) {
        this.gupList.push({ 
          value: gup, 
          label: `${gup} GUP - ${graduacion.nombre}` 
        });
      }
    }

    // Build DAN list (1 to 9)
    this.danList = [];
    for (let dan = 1; dan <= 9; dan++) {
      const graduacion = this.graduaciones.find(g => g.dan === dan);
      if (graduacion) {
        this.danList.push({ 
          value: dan, 
          label: `${dan}° DAN - ${graduacion.nombre}` 
        });
      }
    }
  }

  onGradoTipoChange(): void {
    const gradoTipo = this.searchForm.get('gradoTipo')?.value;
    
    if (gradoTipo === 'GUP') {
      this.searchForm.patchValue({ dan: '' });
    } else if (gradoTipo === 'DAN') {
      this.searchForm.patchValue({ gup: '' });
    } else {
      // TODOS - clear both
      this.searchForm.patchValue({ gup: '', dan: '' });
    }
  }

  // ============================================================================
  // VALIDATION
  // ============================================================================

  private setupRangeValidation(): void {
    this.searchForm.get('nroLibretaDesde')?.valueChanges.subscribe(() => {
      this.validateLibretaRange();
    });
    this.searchForm.get('nroLibretaHasta')?.valueChanges.subscribe(() => {
      this.validateLibretaRange();
    });

    this.searchForm.get('fcNacimientoDesde')?.valueChanges.subscribe(() => {
      this.validateBirthDateRange();
    });
    this.searchForm.get('fcNacimientoHasta')?.valueChanges.subscribe(() => {
      this.validateBirthDateRange();
    });
  }

  private validateLibretaRange(): void {
    const desde = this.searchForm.get('nroLibretaDesde')?.value;
    const hasta = this.searchForm.get('nroLibretaHasta')?.value;

    if (desde && hasta && parseInt(desde) > parseInt(hasta)) {
      this.searchForm.get('nroLibretaHasta')?.setErrors({ rangeInvalid: true });
    } else {
      const currentErrors = this.searchForm.get('nroLibretaHasta')?.errors;
      if (currentErrors?.['rangeInvalid']) {
        delete currentErrors['rangeInvalid'];
        const hasOtherErrors = Object.keys(currentErrors).length > 0;
        this.searchForm.get('nroLibretaHasta')?.setErrors(hasOtherErrors ? currentErrors : null);
      }
    }
  }

  private validateBirthDateRange(): void {
    const desde = this.searchForm.get('fcNacimientoDesde')?.value;
    const hasta = this.searchForm.get('fcNacimientoHasta')?.value;

    this.clearSpecificErrors('fcNacimientoDesde', ['rangeInvalid', 'futureDate']);
    this.clearSpecificErrors('fcNacimientoHasta', ['rangeInvalid', 'futureDate']);

    if (desde && desde > this.TODAY) {
      this.setError('fcNacimientoDesde', 'futureDate');
    }

    if (hasta && hasta > this.TODAY) {
      this.setError('fcNacimientoHasta', 'futureDate');
    }

    if (desde && hasta && desde > hasta) {
      this.setError('fcNacimientoHasta', 'rangeInvalid');
    }
  }

  private setError(controlName: string, errorType: string): void {
    const control = this.searchForm.get(controlName);
    if (control) {
      const currentErrors = control.errors || {};
      currentErrors[errorType] = true;
      control.setErrors(currentErrors);
    }
  }

  private clearSpecificErrors(controlName: string, errorTypes: string[]): void {
    const control = this.searchForm.get(controlName);
    if (control?.errors) {
      const currentErrors = { ...control.errors };
      
      errorTypes.forEach(errorType => {
        delete currentErrors[errorType];
      });
      
      const hasOtherErrors = Object.keys(currentErrors).length > 0;
      control.setErrors(hasOtherErrors ? currentErrors : null);
    }
  }

  hasValidationErrors(): boolean {
    return this.searchForm.invalid;
  }

  getFieldErrorMessage(fieldName: string): string {
    const control = this.searchForm.get(fieldName);
    if (control?.errors) {
      if (control.errors['min']) return 'El valor mínimo es 1';
      if (control.errors['rangeInvalid']) return 'El valor "hasta" debe ser mayor o igual al valor "desde"';
      if (control.errors['futureDate']) return 'La fecha no puede ser en el futuro';
    }
    return '';
  }

  // ============================================================================
  // COMPLEX SEARCH
  // ============================================================================

  filterItfRecords(): void {
    if (this.hasValidationErrors()) {
      this.errorMessage = 'Por favor corrija los errores en el formulario antes de buscar';
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    const filters = this.prepareFilters();

    this.itfDatabaseService.filter(filters).subscribe(
      (data: ItfDatabase[]) => {
        this.itfRecords = data;
        this.applyFilters();
        this.loading = false;
        this.goToPage(1);
      },
      (error: Error) => {
        console.error('Error searching ITF records:', error);
        this.errorMessage = 'Error al buscar registros ITF';
        this.loading = false;
      }
    );
  }

  private prepareFilters(): any {
    const formValues = this.searchForm.value;
    const filters: any = {};

    // Nro Libreta range
    if (formValues.nroLibretaDesde) filters.nroLibretaDesde = formValues.nroLibretaDesde;
    if (formValues.nroLibretaHasta) filters.nroLibretaHasta = formValues.nroLibretaHasta;

    // Text filters
    if (formValues.nombre) filters.nombre = formValues.nombre;
    if (formValues.apellido) filters.apellido = formValues.apellido;
    if (formValues.nuCertDan) filters.nuCertDan = formValues.nuCertDan;
    if (formValues.ngb) filters.ngb = formValues.ngb;
    if (formValues.nacionalidad) filters.nacionalidad = formValues.nacionalidad;
    if (formValues.emailNgb) filters.emailNgb = formValues.emailNgb;

    // Date range
    if (formValues.fcNacimientoDesde) {
      filters.fcNacimientoDesde = this.sharedService.formatDate(formValues.fcNacimientoDesde);
    }
    if (formValues.fcNacimientoHasta) {
      filters.fcNacimientoHasta = this.sharedService.formatDate(formValues.fcNacimientoHasta);
    }

    // Sexo
    if (formValues.sexo && formValues.sexo !== '') {
      filters.sexo = formValues.sexo;
    }

    // Graduacion
    if (formValues.gup) filters.gup = formValues.gup;
    if (formValues.dan) filters.dan = formValues.dan;

    // Matricula
    if (formValues.matriculaVigente === 'SI') filters.matriculaVigente = true;
    if (formValues.matriculaVigente === 'NO') filters.matriculaVigente = false;
    if (formValues.matriculaAnio) filters.matriculaAnio = formValues.matriculaAnio;

    return filters;
  }

  applyFilters(): void {
    this.filteredRecords = [...this.itfRecords];
    this.calculatePagination();
  }

  resetFilters(): void {
    this.searchForm.reset();
    this.searchForm.patchValue({
      sexo: '',
      gradoTipo: '',
      matriculaVigente: ''
    });
    this.filteredRecords = [...this.itfRecords];
    this.calculatePagination();
    this.goToPage(1);
  }

  // ============================================================================
  // SORTING
  // ============================================================================

  onSortChange(field: string): void {
    if (!this.availableSortFields.includes(field)) {
      console.warn(`Invalid sort field: ${field}`);
      return;
    }

    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'asc';
    }
    
    this.filterItfRecords();
  }

  // ============================================================================
  // PAGINATION
  // ============================================================================

  calculatePagination(): void {
    this.totalPages = Math.ceil(this.filteredRecords.length / this.pageSize);
    if (this.totalPages === 0) this.totalPages = 1;
    this.updatePaginatedResults();
  }

  updatePaginatedResults(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.filteredRecords.length);
    this.paginatedRecords = this.filteredRecords.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedResults();
      const resultsContainer = document.querySelector('.results-container');
      if (resultsContainer) {
        resultsContainer.scrollTop = 0;
        resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const totalPagesToShow = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(totalPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + totalPagesToShow - 1);
    
    if (endPage - startPage + 1 < totalPagesToShow) {
      startPage = Math.max(1, endPage - totalPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  // ============================================================================
  // MODAL HANDLERS
  // ============================================================================

  onViewItfDetail(record: ItfDatabase): void {
    this.selectedRecord = record;
    this.showItfDetail = true;
  }

  onCloseItfDetail(): void {
    this.showItfDetail = false;
    this.selectedRecord = null;
    this.filterItfRecords(); // Refresh data
  }

  onViewEstudianteDetail(record: ItfDatabase): void {
    if (!record.nroLibreta) {
      this.errorMessage = 'Este registro no está vinculado a un estudiante';
      return;
    }

    this.loading = true;
    this.estudianteService.getEstudiante(record.nroLibreta).subscribe(
      (estudiante) => {
        this.selectedEstudiante = estudiante;
        this.showEstudianteDetail = true;
        this.loading = false;
      },
      (err) => {
        console.error('Error loading estudiante:', err);
        this.errorMessage = 'Error al cargar el estudiante';
        this.loading = false;
      }
    );
  }

  onCloseEstudianteDetail(): void {
    this.showEstudianteDetail = false;
    this.selectedEstudiante = null;
  }

  // ============================================================================
  // SIMPLE SEARCH
  // ============================================================================

  buscar(): void {
    if (this.searchType === 'libreta') {
      this.buscarPorNroLibreta();
    } else {
      this.buscarPorDocumento();
    }
  }

  // Search by document number
  buscarPorDocumento(): void {
    if (!this.nroDocumento || this.nroDocumento.trim() === '') {
      this.error = 'Debe ingresar un número de documento';
      return;
    }

    // Clear previous state
    this.clearSearchState();

    // Search student by document
    this.estudianteService.filterEstudiantes({ documento: this.nroDocumento }).subscribe(
      (estudiantes) => {
        if (estudiantes && estudiantes.length > 0) {
          const estudiante = estudiantes[0];
          this.estudianteData = estudiante;

          // Check if this student has an ITF record
          if (estudiante.nroLibreta) {
            this.itfDatabaseService.getByNroLibreta(estudiante.nroLibreta).subscribe(
              (records) => {
                if (records && records.length > 0) {
                  // ITF record exists
                  this.itfRecord = records[0];
                  this.isNew = false;
                  this.checkGraduationDiscrepancy(this.itfRecord);
                } else {
                  // No ITF record - show registration prompt
                  this.showRegisterPrompt = true;
                }
              },
              (err) => {
                // No ITF record found - show registration prompt
                this.showRegisterPrompt = true;
              }
            );
          } else {
            // Student found but no nroLibreta (shouldn't happen, but handle it)
            this.error = 'El estudiante no tiene número de libreta asignado';
          }
        } else {
          // No student found with this document
          this.error = `No se encontró ningún estudiante con documento ${this.nroDocumento}`;
        }
      },
      (err) => {
        console.error('Error searching by document:', err);
        this.error = 'Error al buscar por documento';
      }
    );
  }

  buscarPorNroLibreta(): void {
    if (this.nroLibreta === null) {
      this.error = 'Debe ingresar un número de libreta';
      return;
    }

    // Clear previous state
    this.clearSearchState();

    // First, check if ITF record exists
    this.itfDatabaseService.getByNroLibreta(this.nroLibreta).subscribe(
      (records) => {
        if (records && records.length > 0) {
          // ITF record exists - show it AS IS (don't overwrite)
          this.itfRecord = records[0];
          this.isNew = false;
          this.checkGraduationDiscrepancy(this.itfRecord);
        } else {
          // No ITF record - check if estudiante exists
          this.checkEstudianteExists();
        }
      },
      (err) => {
        this.checkEstudianteExists();
      }
    );
  }

  // Clear search state helper
  private clearSearchState(): void {
    this.itfRecord = null;
    this.isNew = false;
    this.error = null;
    this.showRegisterPrompt = false;
    this.estudianteData = null;
    this.graduationWarning = null;
  }

  // Switch search type
  onSearchTypeChange(): void {
    this.clearSearchState();
    // Clear the unused search field
    if (this.searchType === 'libreta') {
      this.nroDocumento = '';
    } else {
      this.nroLibreta = null;
    }
  }

  // Check if student has higher graduation than ITF record
  private checkGraduationDiscrepancy(itfRecord: ItfDatabase): void {
    if (!itfRecord.nroLibreta) {
      return; // No student link
    }

    this.estudianteService.getEstudiante(itfRecord.nroLibreta).subscribe(
      (estudiante) => {
        if (!estudiante.idGraduacionActual) {
          return; // Student has no graduation
        }

        const estudianteGrad = this.graduaciones.find(g => g.id === estudiante.idGraduacionActual);
        if (!estudianteGrad) {
          return;
        }

        // Get ITF graduation
        let itfGrad: Graduacion | undefined;
        if (itfRecord.dan !== null && itfRecord.dan !== undefined) {
          itfGrad = this.graduaciones.find(g => g.dan === itfRecord.dan);
        } else if (itfRecord.gup !== null && itfRecord.gup !== undefined) {
          itfGrad = this.graduaciones.find(g => g.gup === itfRecord.gup);
        }

        if (!itfGrad) {
          return;
        }

        // Compare: Student has HIGHER graduation than ITF?
        const estudianteIsHigher = this.isGraduationHigher(estudianteGrad, itfGrad);
        
        if (estudianteIsHigher) {
          this.graduationWarning = `⚠️ El estudiante tiene una graduación superior (${estudianteGrad.nombre}) a la registrada en ITF (${itfGrad.nombre}). Considere actualizar el registro ITF.`;
        }
      },
      (err) => {
        // Silent fail - student might have been deleted
      }
    );
  }

  // Compare two graduations
  private isGraduationHigher(grad1: Graduacion, grad2: Graduacion): boolean {
    // Both are DANs - compare numbers (higher is better)
    if (grad1.dan !== null && grad1.dan !== undefined && 
        grad2.dan !== null && grad2.dan !== undefined) {
      return grad1.dan > grad2.dan;
    }

    // grad1 is DAN, grad2 is GUP - DAN is always higher
    if ((grad1.dan !== null && grad1.dan !== undefined) && 
        (grad2.gup !== null && grad2.gup !== undefined)) {
      return true;
    }

    // grad1 is GUP, grad2 is DAN - GUP is never higher
    if ((grad1.gup !== null && grad1.gup !== undefined) && 
        (grad2.dan !== null && grad2.dan !== undefined)) {
      return false;
    }

    // Both are GUPs - compare numbers (LOWER gup number is better!)
    if (grad1.gup !== null && grad1.gup !== undefined && 
        grad2.gup !== null && grad2.gup !== undefined) {
      return grad1.gup < grad2.gup; // 1 GUP > 10 GUP
    }

    return false;
  }

  private checkEstudianteExists(): void {
    if (this.nroLibreta === null) return;

    this.estudianteService.getEstudiante(this.nroLibreta).subscribe(
      (estudiante) => {
        this.estudianteData = estudiante;
        this.showRegisterPrompt = true;
        this.error = null; // Clear error since we found the student
      },
      (err) => {
        if (err.status === 404) {
          this.estudianteData = null;
          this.showRegisterPrompt = true;
          this.error = null; // Clear error, show prompt instead
        } else {
          this.error = 'Error al buscar estudiante';
        }
      }
    );
  }

  registerNewRecord(): void {
    if (this.estudianteData) {
      // Only use student data for NEW records
      this.itfRecord = this.createFromEstudiante(this.estudianteData);
    } else {
      this.itfRecord = new ItfDatabase(
        null,
        this.nroLibreta,
        '',
        '',
        undefined,
        undefined,
        null,
        null,
        null,
        'ACADEMIA HAKKYO',
        'ARGENTINA'
      );
    }
    
    this.isNew = true;
    this.showRegisterPrompt = false;
    this.error = null;
  }

  registerWithoutNroLibreta(): void {
    this.itfRecord = new ItfDatabase(
      null,
      null,
      '',
      '',
      undefined,
      undefined,
      null,
      null,
      null,
      'ACADEMIA HAKKYO',
      'ARGENTINA'
    );
    
    this.isNew = true;
    this.showRegisterPrompt = false;
    this.error = null;
  }

  // This method is ONLY used for NEW records
  private createFromEstudiante(estudiante: Estudiante): ItfDatabase {
    let gup: number | null = null;
    let dan: number | null = null;
    let nuCertDan: string | null = null;

    if (estudiante.idGraduacionActual) {
      const graduacionActual = this.graduaciones.find(
        (g: Graduacion) => g.id === estudiante.idGraduacionActual
      );

      if (graduacionActual) {
        if (graduacionActual.gup !== null && graduacionActual.gup !== undefined) {
          gup = graduacionActual.gup;
        } else if (graduacionActual.dan !== null && graduacionActual.dan !== undefined) {
          dan = graduacionActual.dan;
          nuCertDan = `AR-${graduacionActual.dan}-`;
        }
      }
    }

    return new ItfDatabase(
      null,
      estudiante.nroLibreta,
      estudiante.nombre,
      estudiante.apellido,
      estudiante.fcNacimiento,
      undefined,
      gup,
      nuCertDan,
      dan,
      'ACADEMIA HAKKYO',
      estudiante.nacionalidad || 'ARGENTINA',
      estudiante.email
    );
  }

  handleCancelEdit(): void {
    this.itfRecord = null;
    this.isNew = false;
    this.error = null;
    this.showRegisterPrompt = false;
    this.estudianteData = null;
    this.graduationWarning = null;
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  formatDate(date?: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-AR');
  }

  formatCurrency(amount?: number | null): string {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  }

  getGraduacionDisplay(record: ItfDatabase): string {
    if (record.dan !== null && record.dan !== undefined) {
      const grad = this.graduaciones.find(g => g.dan === record.dan);
      return grad ? grad.nombre : `${record.dan}° DAN`;
    } else if (record.gup !== null && record.gup !== undefined) {
      const grad = this.graduaciones.find(g => g.gup === record.gup);
      return grad ? grad.nombre : `${record.gup} GUP`;
    }
    return 'N/A';
  }

  getPaymentStatusBadge(record: ItfDatabase): { text: string; class: string } {
    if (record.matriculaVigente) {
      return { text: 'VIGENTE', class: 'badge-vigente' };
    }
    if (!record.matriculaAnio) {
      return { text: 'SIN PAGO', class: 'badge-sin-pago' };
    }
    return { text: 'VENCIDO', class: 'badge-vencido' };
  }

  getUsernameById(id?: number | null): string {
    // TODO: Implement user lookup if you have a user service
    return id ? `User #${id}` : 'N/A';
  }

  // Payment modal handlers
  onOpenPaymentModal(record: ItfDatabase, event: Event): void {
    event.stopPropagation(); // Prevent row click
    this.selectedRecordForPayment = record;
    this.showPaymentModal = true;
  }

  onClosePaymentModal(): void {
    this.showPaymentModal = false;
    this.selectedRecordForPayment = null;
  }

  onPaymentRegistered(pago: ItfPago): void {
    // Refresh the data to show updated payment status
    this.filterItfRecords();
    
    // Show success message
    this.successMessage = `Pago registrado exitosamente para el año ${pago.anio}`;
    setTimeout(() => this.successMessage = null, 3000);
  }

  resetSearch(): void {
    this.nroLibreta = null;
    this.nroDocumento = '';
    this.searchType = 'libreta';
    this.itfRecord = null;
    this.isNew = false;
    this.error = null;
    this.showRegisterPrompt = false;
    this.estudianteData = null;
    this.graduationWarning = null;
    this.searchForm.reset();
    this.itfRecords = [];
    this.filteredRecords = [];
  }
}